import { RoleModuleApi } from "../apis/RoleModuleApi";
import { getCurrentLanguage } from "./language_helper";
import { getUserID } from "../Token";

/**
 * Cache for the sidebar menu (GET /menu).
 *
 * Why this exists:
 *  - App.js renders one <Authmiddleware layout={Layout}> per route inside a
 *    <Switch>, each keyed by index, so React remounts the whole Layout on
 *    every navigation. That remounts SidebarContent and re-issued GET /menu
 *    on every single page change.
 *  - On a hard refresh there was nothing to render until that request came
 *    back, so the sidebar sat empty for the whole round trip.
 *
 * Strategy: stale-while-revalidate.
 *  - the menu is served instantly from memory (survives remounts) or from
 *    sessionStorage (survives a refresh)
 *  - a background request still runs so counts stay fresh
 *  - concurrent callers share one in-flight request instead of stampeding
 *
 * The cache is scoped by user id AND language, so switching account or
 * language can never show another context's menu.
 */

const STORAGE_PREFIX = "SALON_MENU_CACHE_";

let memoryCache = {};
let inFlight = {};

const cacheKey = (language) => `${STORAGE_PREFIX}${getUserID() || "anon"}_${language}`;

const readSession = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null; // private mode / corrupted entry
  }
};

const writeSession = (key, modules) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(modules));
  } catch (e) {
    /* quota or private mode - the memory cache still works */
  }
};

/** Menu to paint immediately, or null when nothing is cached yet. */
export const getCachedMenu = (language = getCurrentLanguage()) => {
  const key = cacheKey(language);
  if (memoryCache[key]) return memoryCache[key];

  const stored = readSession(key);
  if (stored) memoryCache[key] = stored;
  return stored;
};

/**
 * Fetch the menu and refresh the cache. Concurrent calls for the same
 * user+language share a single request.
 */
export const fetchMenu = (language = getCurrentLanguage()) => {
  const key = cacheKey(language);
  if (inFlight[key]) return inFlight[key];

  const request = RoleModuleApi.getAllModule()
    .then((res) => {
      const modules = res && res.data && res.data.data;
      if (Array.isArray(modules)) {
        memoryCache[key] = modules;
        writeSession(key, modules);
        return modules;
      }
      return getCachedMenu(language);
    })
    .finally(() => {
      delete inFlight[key];
    });

  inFlight[key] = request;
  return request;
};

/** Drop everything - call on logout so the next user starts clean. */
export const clearMenuCache = () => {
  memoryCache = {};
  inFlight = {};
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.indexOf(STORAGE_PREFIX) === 0)
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) {
    /* nothing to clear */
  }
};

export default { getCachedMenu, fetchMenu, clearMenuCache };
